/**
 * Service Lifecycle Management
 * 
 * Handles creating services, assigning/releasing workers,
 * ending services with deposit settlement, and monthly billing.
 */

import { supabase } from '../lib/supabase';
import { calculateClientServiceDaysFromAttendance } from '../utils/billingRate';
import { generateAndUploadInvoicePdf } from '../utils/generateInvoicePdf';
import { format } from 'date-fns';

// ── Types ─────────────────────────────────────────────────

export interface Service {
    id: string;
    client_id: string;
    lead_id: string | null;
    service_type: string | null;
    hours_per_day: number;
    start_date: string;
    end_date: string | null;
    status: 'active' | 'ended';
    deposit_amount: number;
    deposit_status: 'pending' | 'collected' | 'settled';
    complete_month_daily_rate: number;
    incomplete_month_daily_rate: number;
    notes: string | null;
    legacy_assignment_id: string | null;
    created_at: string;
    updated_at: string;
}

export interface ServiceWorkerAssignment {
    id: string;
    service_id: string;
    employee_id: string;
    start_date: string;
    end_date: string | null;
    created_at: string;
    updated_at: string;
}

export interface ServiceBill {
    id: string;
    service_id: string;
    period_start: string;
    period_end: string;
    total_days: number;
    daily_rate_used: number;
    amount: number;
    type: 'recurring' | 'final';
    deposit_applied: number;
    deposit_settled: boolean;
    settlement_amount: number;
    notes: string | null;
    created_at: string;
}

export interface ServiceWithDetails extends Service {
    clients?: { client_name: string; phone_number: string | null };
    service_worker_assignments?: (ServiceWorkerAssignment & {
        employees?: { id: string; full_name: string; job_title: string; photo_url: string | null; status: string };
    })[];
    service_bills?: ServiceBill[];
}

async function enrichServicesWithPayments(services: any[]): Promise<ServiceWithDetails[]> {
    if (!services || services.length === 0) return [];
    try {
        const [paymentsRes, quotesRes, leadsRes] = await Promise.all([
            supabase.from('payments').select('client_name, amount, payment_type').eq('payment_type', 'deposit'),
            supabase.from('crm_quotations').select('lead_id, complete_month_rate, incomplete_month_rate, deposit').order('created_at', { ascending: false }),
            supabase.from('crm_leads').select('id, complete_month_daily_rate, incomplete_month_daily_rate'),
        ]);

        const depositMap = new Map<string, number>();
        if (paymentsRes.data) {
            for (const p of paymentsRes.data) {
                const name = (p.client_name || '').trim().toLowerCase();
                if (name) {
                    depositMap.set(name, (depositMap.get(name) || 0) + (p.amount || 0));
                }
            }
        }

        const quotesMap = new Map<string, any>();
        if (quotesRes.data) {
            for (const q of quotesRes.data) {
                if (q.lead_id && !quotesMap.has(q.lead_id)) {
                    quotesMap.set(q.lead_id, q);
                }
            }
        }

        const leadsMap = new Map<string, any>();
        if (leadsRes.data) {
            for (const l of leadsRes.data) {
                if (l.id) leadsMap.set(l.id, l);
            }
        }

        return services.map(s => {
            const clientName = (s.clients?.client_name || '').trim().toLowerCase();
            const collectedDeposit = depositMap.get(clientName) || 0;
            const quote = quotesMap.get(s.client_id) || quotesMap.get(s.lead_id || '');
            const lead = leadsMap.get(s.client_id) || leadsMap.get(s.lead_id || '');

            const depositAmt = (s.deposit_amount && s.deposit_amount > 0) 
                ? s.deposit_amount 
                : (quote?.deposit || collectedDeposit);

            // Respect s.deposit_status as definitive authority; only fall back to checking if payment was collected
            const depositStatus = s.deposit_status 
                ? s.deposit_status 
                : (collectedDeposit >= depositAmt && depositAmt > 0 ? 'collected' : 'pending');

            // Resolve agreed daily rates
            const cmRate = (quote?.complete_month_rate && quote.complete_month_rate > 0)
                ? quote.complete_month_rate
                : (lead?.complete_month_daily_rate && lead.complete_month_daily_rate > 0)
                    ? lead.complete_month_daily_rate
                    : s.complete_month_daily_rate;

            const imRate = (quote?.incomplete_month_rate && quote.incomplete_month_rate > 0)
                ? quote.incomplete_month_rate
                : (lead?.incomplete_month_daily_rate && lead.incomplete_month_daily_rate > 0)
                    ? lead.incomplete_month_daily_rate
                    : s.incomplete_month_daily_rate;

            return {
                ...s,
                deposit_amount: depositAmt,
                deposit_status: depositStatus,
                complete_month_daily_rate: cmRate || 0,
                incomplete_month_daily_rate: imRate || 0,
            };
        });
    } catch {
        return services as ServiceWithDetails[];
    }
}

export async function getActiveServices(): Promise<ServiceWithDetails[]> {
    const { data, error } = await supabase
        .from('services')
        .select(`
            *,
            clients(client_name, phone_number),
            service_worker_assignments(
                *,
                employees(id, full_name, job_title, photo_url, status)
            ),
            service_bills(*)
        `)
        .eq('status', 'active')
        .order('created_at', { ascending: false });

    if (error) throw new Error(`Failed to fetch services: ${error.message}`);
    const valid = (data || []).filter(s => (s.service_worker_assignments || []).length > 0 || (s.service_bills || []).length > 0);
    return enrichServicesWithPayments(valid);
}

export async function getAllServices(): Promise<ServiceWithDetails[]> {
    const { data, error } = await supabase
        .from('services')
        .select(`
            *,
            clients(client_name, phone_number),
            service_worker_assignments(
                *,
                employees(id, full_name, job_title, photo_url, status)
            ),
            service_bills(*)
        `)
        .order('created_at', { ascending: false });

    if (error) throw new Error(`Failed to fetch services: ${error.message}`);
    return enrichServicesWithPayments(data || []);
}

export async function getServiceById(serviceId: string): Promise<ServiceWithDetails | null> {
    const { data, error } = await supabase
        .from('services')
        .select(`
            *,
            clients(client_name, phone_number),
            service_worker_assignments(
                *,
                employees(id, full_name, job_title, photo_url, status)
            ),
            service_bills(*)
        `)
        .eq('id', serviceId)
        .single();

    if (error) return null;
    return data as ServiceWithDetails;
}

// ── Create Service ────────────────────────────────────────

export interface CreateServiceInput {
    client_id: string;
    lead_id?: string;
    service_type?: string;
    hours_per_day?: number;
    start_date: string;
    end_date?: string;
    deposit_amount?: number;
    deposit_status?: 'pending' | 'collected' | 'settled';
    complete_month_daily_rate: number;
    incomplete_month_daily_rate: number;
    notes?: string;
}

export async function createService(input: CreateServiceInput): Promise<Service> {
    const { data, error } = await supabase
        .from('services')
        .insert({
            client_id: input.client_id,
            lead_id: input.lead_id || input.client_id,
            service_type: input.service_type || null,
            hours_per_day: input.hours_per_day || 10,
            start_date: input.start_date,
            end_date: input.end_date || null,
            deposit_amount: input.deposit_amount || 0,
            deposit_status: input.deposit_status || 'pending',
            complete_month_daily_rate: input.complete_month_daily_rate,
            incomplete_month_daily_rate: input.incomplete_month_daily_rate,
            notes: input.notes || null,
        })
        .select()
        .single();

    if (error) throw new Error(`Failed to create service: ${error.message}`);
    return data as Service;
}

// ── Assign Worker to Service ──────────────────────────────

export async function assignWorkerToService(
    serviceId: string,
    employeeId: string,
    startDate: string
): Promise<ServiceWorkerAssignment> {
    // ── Guard: Ensure worker is not already active with another client ──
    const { data: targetSvc } = await supabase
        .from('services')
        .select('id, client_id, lead_id')
        .eq('id', serviceId)
        .maybeSingle();

    const targetClientId = targetSvc?.client_id || targetSvc?.lead_id;

    const { data: existingSwa } = await supabase
        .from('service_worker_assignments')
        .select('id, service_id, services(id, status, client_id, lead_id, crm_leads(name))')
        .eq('employee_id', employeeId)
        .is('end_date', null);

    const conflictingSwa = (existingSwa || []).find((a: any) =>
        a.services &&
        (a.services.status === 'active' || a.services.status === 'pending') &&
        a.service_id !== serviceId &&
        a.services.client_id !== targetClientId &&
        a.services.lead_id !== targetClientId
    );

    if (conflictingSwa) {
        const clientName = (conflictingSwa as any).services?.crm_leads?.name || 'another client';
        const { data: emp } = await supabase.from('employees').select('full_name').eq('id', employeeId).maybeSingle();
        const workerName = emp?.full_name || 'Worker';
        throw new Error(`${workerName} is currently deployed to ${clientName}. Please release or end their active deployment first.`);
    }

    const { data: legacyActive } = await supabase
        .from('worker_assignments')
        .select('id, client_id, clients(client_name)')
        .eq('employee_id', employeeId)
        .eq('assignment_status', 'active')
        .is('end_date', null);

    const conflictingLegacy = (legacyActive || []).find((a: any) => a.client_id !== targetClientId);
    if (conflictingLegacy) {
        const clientName = (conflictingLegacy as any).clients?.client_name || 'another client';
        const { data: emp } = await supabase.from('employees').select('full_name').eq('id', employeeId).maybeSingle();
        const workerName = emp?.full_name || 'Worker';
        throw new Error(`${workerName} is currently deployed to ${clientName}. Please release or end their active deployment first.`);
    }

    // Check if worker is already actively assigned to this service
    const { data: activeForCurrentService } = await supabase
        .from('service_worker_assignments')
        .select('*')
        .eq('service_id', serviceId)
        .eq('employee_id', employeeId)
        .is('end_date', null)
        .maybeSingle();

    if (activeForCurrentService) {
        return activeForCurrentService as unknown as ServiceWorkerAssignment;
    }

    const { data, error } = await supabase
        .from('service_worker_assignments')
        .insert({
            service_id: serviceId,
            employee_id: employeeId,
            start_date: startDate,
        })
        .select()
        .single();

    if (error) throw new Error(`Failed to assign worker: ${error.message}`);

    // Update employee status
    await supabase.from('employees')
        .update({ status: 'assigned', updated_at: new Date().toISOString() })
        .eq('id', employeeId);

    // Ensure legacy worker_assignments record exists for complete CRM & billing synchronization
    if (targetClientId) {
        try {
            const { data: existingAsgn } = await supabase
                .from('worker_assignments')
                .select('id')
                .eq('client_id', targetClientId)
                .eq('employee_id', employeeId)
                .eq('assignment_status', 'active')
                .maybeSingle();

            if (!existingAsgn) {
                await supabase.from('worker_assignments').insert([{
                    client_id: targetClientId,
                    employee_id: employeeId,
                    start_date: startDate,
                    assignment_status: 'active',
                    service_type: 'date_range',
                    hours_per_day: 10,
                }]);
            }
        } catch (asgnSyncErr) {
            console.warn('Failed to sync to worker_assignments in assignWorkerToService:', asgnSyncErr);
        }
    }

    return data as ServiceWorkerAssignment;
}

// ── Restart Service for Client ────────────────────────────

export interface RestartWorkerAssignment {
    workerId: string;
    workerPayoutRate?: number;
}

export interface RestartClientServiceInput {
    clientId: string;
    clientName: string;
    serviceType: string;
    hoursPerDay: number;
    startDate: string;
    endDate?: string | null;
    completeMonthDailyRate: number;
    incompleteMonthDailyRate: number;
    depositAmount: number;
    depositStatus: 'collected' | 'pending';
    depositPaymentMethod?: string;
    workerId?: string;
    workerPayoutRate?: number;
    workers?: RestartWorkerAssignment[];
    notes?: string;
    careScope?: {
        patientName?: string;
        age?: string;
        ageUnit?: string;
        weight?: string;
        relativeName?: string;
        contactNumber?: string;
        alternateContactNumber?: string;
        address?: string;
        medicalCondition?: string;
        formType?: 'baby_care_form' | 'patient_care_form';
        duties?: string[];
        instructions?: string;
    };
}

export async function restartClientService(input: RestartClientServiceInput): Promise<{
    success: boolean;
    serviceId?: string;
    error?: string;
}> {
    try {
        // Pre-validate workers are not already assigned to another client
        const workersToCheck: RestartWorkerAssignment[] = input.workers && input.workers.length > 0
            ? input.workers
            : (input.workerId ? [{ workerId: input.workerId, workerPayoutRate: input.workerPayoutRate }] : []);

        for (const w of workersToCheck) {
            const { data: existingSwa } = await supabase
                .from('service_worker_assignments')
                .select('id, service_id, services(id, status, client_id, lead_id, crm_leads(name))')
                .eq('employee_id', w.workerId)
                .is('end_date', null);

            const conflicting = (existingSwa || []).find((a: any) =>
                a.services &&
                (a.services.status === 'active' || a.services.status === 'pending') &&
                a.services.client_id !== input.clientId &&
                a.services.lead_id !== input.clientId
            );

            if (conflicting) {
                const clientName = (conflicting as any).services?.crm_leads?.name || 'another client';
                const { data: emp } = await supabase.from('employees').select('full_name').eq('id', w.workerId).maybeSingle();
                const workerName = emp?.full_name || 'Selected worker';
                return {
                    success: false,
                    error: `${workerName} is currently deployed to ${clientName}. Please release them before reassigning.`
                };
            }

            const { data: legacyActive } = await supabase
                .from('worker_assignments')
                .select('id, client_id, clients(client_name)')
                .eq('employee_id', w.workerId)
                .eq('assignment_status', 'active')
                .is('end_date', null);

            const conflictingLegacy = (legacyActive || []).find((a: any) => a.client_id !== input.clientId);
            if (conflictingLegacy) {
                const clientName = (conflictingLegacy as any).clients?.client_name || 'another client';
                const { data: emp } = await supabase.from('employees').select('full_name').eq('id', w.workerId).maybeSingle();
                const workerName = emp?.full_name || 'Selected worker';
                return {
                    success: false,
                    error: `${workerName} is currently deployed to ${clientName}. Please release them before reassigning.`
                };
            }
        }
        // 0. Ensure client exists in clients table with matching UUID before creating service
        await supabase.from('clients').upsert({
            id: input.clientId,
            client_name: input.clientName,
            created_at: new Date().toISOString()
        }, { onConflict: 'id' });

        // 0b. Archive any existing active service for this client so previous cycle and settled deposit are preserved
        try {
            const { data: activeExistingServices } = await supabase
                .from('services')
                .select('id, deposit_amount')
                .eq('client_id', input.clientId)
                .eq('status', 'active');

            if (activeExistingServices && activeExistingServices.length > 0) {
                for (const oldSvc of activeExistingServices) {
                    await supabase.from('services').update({
                        status: 'ended',
                        end_date: input.startDate,
                        deposit_status: 'settled',
                        updated_at: new Date().toISOString()
                    }).eq('id', oldSvc.id);
                }
            }
        } catch (archiveErr) {
            console.warn('Failed to archive previous active services on restart:', archiveErr);
        }

        // 1. Create new service record in services table
        let serviceNotes = input.notes || undefined;
        if (input.careScope) {
            serviceNotes = JSON.stringify({
                patient_name: input.careScope.patientName || input.clientName,
                age: input.careScope.age || '',
                age_unit: input.careScope.ageUnit || 'Years',
                weight: input.careScope.weight || '',
                relative_name: input.careScope.relativeName || input.clientName,
                contact_number: input.careScope.contactNumber || '',
                alternate_contact_number: input.careScope.alternateContactNumber || '',
                address: input.careScope.address || '',
                medical_condition: input.careScope.medicalCondition || '',
                form_type: input.careScope.formType || 'patient_care_form',
                duties: input.careScope.duties || [],
                instructions: input.careScope.instructions || input.notes || '',
                other_work: input.careScope.instructions || input.notes || '',
                updated_at: new Date().toISOString()
            });
        }

        const service = await createService({
            client_id: input.clientId,
            service_type: input.serviceType,
            hours_per_day: input.hoursPerDay,
            start_date: input.startDate,
            end_date: input.endDate || undefined,
            complete_month_daily_rate: input.completeMonthDailyRate,
            incomplete_month_daily_rate: input.incompleteMonthDailyRate,
            deposit_amount: input.depositAmount,
            deposit_status: input.depositStatus === 'collected' ? 'collected' : 'pending',
            notes: serviceNotes,
        });

        // 2. Assign workers to service (multi-worker support)
        const workersList: RestartWorkerAssignment[] = input.workers && input.workers.length > 0
            ? input.workers
            : (input.workerId ? [{ workerId: input.workerId, workerPayoutRate: input.workerPayoutRate }] : []);

        const assignedWorkerNames: string[] = [];

        for (const w of workersList) {
            // Cancel any stale active assignment for this client/employee to prevent duplicates
            await supabase
                .from('worker_assignments')
                .update({ assignment_status: 'cancelled', notes: 'Superseded by service restart' })
                .eq('client_id', input.clientId)
                .eq('employee_id', w.workerId)
                .eq('assignment_status', 'active');

            // 1. Create official worker_assignments record with full service metadata
            const { data: newAsgn, error: asgnError } = await supabase.from('worker_assignments').insert([{
                client_id: input.clientId,
                employee_id: w.workerId,
                start_date: input.startDate,
                end_date: input.endDate || null,
                assignment_status: 'active',
                service_type: 'date_range',
                hours_per_day: input.hoursPerDay || 10,
                client_billing_rate: input.completeMonthDailyRate,
                deposit_amount: input.depositAmount,
                deposit_paid: input.depositStatus === 'collected' ? input.depositAmount : 0,
                assigned_at: new Date().toISOString(),
                notes: input.serviceType,
            }]).select();

            if (asgnError) {
                console.error('Error inserting worker_assignment:', asgnError);
            } else if (newAsgn && newAsgn[0]) {
                await supabase.from('services').update({ legacy_assignment_id: newAsgn[0].id }).eq('id', service.id);
            }

            // 2. Link service_worker_assignments (its internal check will find the existingAsgn created above and skip duplicate insertion)
            await assignWorkerToService(service.id, w.workerId, input.startDate);

            // Fetch employee details to update status and record name for CRM
            const { data: empData } = await supabase.from('employees').select('full_name, job_title').eq('id', w.workerId).single();
            if (empData?.full_name) {
                assignedWorkerNames.push(empData.full_name);
            }

            // Update employee status & assigned client name
            await supabase.from('employees').update({
                status: 'assigned',
                assigned_client: input.clientName,
                updated_at: new Date().toISOString()
            }).eq('id', w.workerId);

            // Auto-initialize attendance row if service start date is today or in past
            const todayStr = format(new Date(), 'yyyy-MM-dd');
            if (input.startDate <= todayStr && newAsgn && newAsgn[0]) {
                await supabase.from('attendance').insert([{
                    worker_id: w.workerId,
                    assignment_id: newAsgn[0].id,
                    duty_date: input.startDate,
                    status: 'Present',
                    is_half_day: false,
                    is_absent: false,
                    hours_worked: input.hoursPerDay || 10,
                    notes: `Initialized on service restart (${input.serviceType})`
                }]);
            }
        }

        // 3. If deposit is marked as collected, record in payments table
        if (input.depositStatus === 'collected' && input.depositAmount > 0) {
            await supabase.from('payments').insert([{
                client_name: input.clientName,
                amount: input.depositAmount,
                payment_type: 'deposit',
                payment_method: input.depositPaymentMethod || 'UPI',
                payment_date: input.startDate,
                status: 'completed',
                notes: `Security deposit for restarted service (${input.serviceType})`
            }]);
        }

        // 4. Update CRM lead pipeline stage to Active Client and sync service role / notes
        const { data: currentLead } = await supabase
            .from('crm_leads')
            .select('notes')
            .eq('id', input.clientId)
            .maybeSingle();

        let updatedNotes = currentLead?.notes || '';
        if (updatedNotes) {
            if (updatedNotes.match(/Service:\s*[^\n\r]+/i)) {
                updatedNotes = updatedNotes.replace(/Service:\s*[^\n\r]+/i, `Service: ${input.serviceType}`);
            } else {
                updatedNotes = `Service: ${input.serviceType}\n` + updatedNotes;
            }
        } else {
            updatedNotes = `Service: ${input.serviceType}`;
        }

        const leadUpdatePayload: any = {
            pipeline_stage: 'Active Client',
            assigned_worker_role: input.serviceType,
            notes: updatedNotes,
            estimated_value_monthly: input.completeMonthDailyRate * 30,
            complete_month_daily_rate: input.completeMonthDailyRate,
            incomplete_month_daily_rate: input.incompleteMonthDailyRate,
            updated_at: new Date().toISOString()
        };
        if (assignedWorkerNames.length > 0) {
            leadUpdatePayload.assigned_worker_name = assignedWorkerNames.join(', ');
        }

        // Synchronize careScope with CRM lead & client_consents
        if (input.careScope) {
            if (input.careScope.address) {
                leadUpdatePayload.location = input.careScope.address;
            }
            if (input.careScope.duties || input.careScope.formType) {
                const workFormPayload = {
                    form_type: input.careScope.formType || 'patient_care_form',
                    patient_name: input.careScope.patientName || input.clientName,
                    duties: input.careScope.duties || [],
                    other_work: input.careScope.instructions || input.notes || '',
                    address: input.careScope.address || undefined,
                    updated_at: new Date().toISOString()
                };
                leadUpdatePayload.work_form_data = workFormPayload;
                try {
                    await supabase.from('client_work_forms').insert([{
                        lead_id: input.clientId,
                        form_type: input.careScope.formType || 'patient_care_form',
                        patient_name: input.careScope.patientName || input.clientName,
                        duties: input.careScope.duties || [],
                        other_work: input.careScope.instructions || input.notes || ''
                    }]);
                } catch (e) {
                    console.warn('Could not mirror to client_work_forms', e);
                }
            }

            // Sync client_consents table for repeat service
            try {
                await supabase.from('client_consents').insert([{
                    lead_id: input.clientId,
                    phone: input.careScope.contactNumber || undefined,
                    patient_name: input.careScope.patientName || input.clientName,
                    age: input.careScope.age || null,
                    age_unit: input.careScope.ageUnit || 'Years',
                    weight: input.careScope.weight || null,
                    relative_name: input.careScope.relativeName || input.clientName,
                    contact_number: input.careScope.contactNumber || null,
                    alternate_contact_number: input.careScope.alternateContactNumber || null,
                    address: input.careScope.address || null,
                    other_details: input.careScope.medicalCondition || null,
                    service_category: input.serviceType,
                    service_start_date: input.startDate,
                    offered_time: input.hoursPerDay === 24 ? '24 Hours (Live-in)' : '10 Hours',
                    terms_accepted: true,
                }]);
            } catch (cErr) {
                console.warn('Non-fatal error creating client_consent on restart:', cErr);
            }
        }

        const { error: leadUpdateError } = await supabase
            .from('crm_leads')
            .update(leadUpdatePayload)
            .eq('id', input.clientId);

        if (leadUpdateError) {
            console.error('Error updating CRM lead to Active Client:', leadUpdateError);
        }

        return { success: true, serviceId: service.id };
    } catch (err: any) {
        console.error('restartClientService error:', err);
        return { success: false, error: err.message || 'Failed to restart service' };
    }
}

// ── Release Worker (calls RPC) ────────────────────────────

export interface ReleaseWorkerResult {
    success: boolean;
    assignment_id?: string;
    days_counted?: number;
    payroll_id?: string;
    error?: string;
}

export async function releaseWorker(
    assignmentId: string,
    releaseDate?: string
): Promise<ReleaseWorkerResult> {
    const todayStr = format(new Date(), 'yyyy-MM-dd');
    let effectiveReleaseDate = releaseDate || todayStr;

    // Check if there is any attendance logged for this worker on or after effectiveReleaseDate
    try {
        const { data: asgn } = await supabase
            .from('service_worker_assignments')
            .select('employee_id, service_id, services(client_id)')
            .eq('id', assignmentId)
            .maybeSingle();

        if (asgn?.employee_id) {
            const { data: att } = await supabase
                .from('attendance')
                .select('duty_date')
                .eq('worker_id', asgn.employee_id)
                .order('duty_date', { ascending: false })
                .limit(1)
                .maybeSingle();

            if (att?.duty_date && att.duty_date > effectiveReleaseDate) {
                effectiveReleaseDate = att.duty_date;
            }

            // Sync legacy worker_assignments record if present
            const clientId = (asgn.services as any)?.client_id;
            if (clientId) {
                await supabase.from('worker_assignments').update({
                    assignment_status: 'completed',
                    end_date: effectiveReleaseDate
                }).eq('employee_id', asgn.employee_id).eq('assignment_status', 'active');
            }
        }
    } catch (err) {
        console.warn('Error checking latest attendance for worker release:', err);
    }

    const { data, error } = await supabase.rpc('release_worker', {
        p_assignment_id: assignmentId,
        p_release_date: effectiveReleaseDate,
    });

    if (error) throw new Error(`Failed to release worker: ${error.message}`);
    return data as ReleaseWorkerResult;
}

// ── End Service (calls RPC) ───────────────────────────────

export interface EndServiceResult {
    success: boolean;
    service_id?: string;
    client_id?: string;
    client_name?: string;
    client_phone?: string;
    total_lifetime_days?: number;
    calendar_days?: number;
    rate_used?: number;
    rate_tier?: 'incomplete_month' | 'complete_month';
    tier_reason?: string;
    is_incomplete_month?: boolean;
    true_cost?: number;
    previously_billed?: number;
    deposit?: number;
    settlement?: number;
    workers_released?: number;
    invoice_number?: string;
    invoice_pdf_url?: string;
    error?: string;
}

export async function endService(
    serviceId: string,
    endDate?: string
): Promise<EndServiceResult> {
    const effectiveEndDate = endDate || format(new Date(), 'yyyy-MM-dd');

    // 1. Fetch service details with worker assignments and client info
    const { data: service, error: svcError } = await supabase
        .from('services')
        .select(`
            id,
            client_id,
            start_date,
            end_date,
            status,
            complete_month_daily_rate,
            incomplete_month_daily_rate,
            deposit_amount,
            service_type,
            hours_per_day,
            clients (id, client_name, phone_number),
            service_worker_assignments (
                id,
                employee_id,
                start_date,
                end_date
            )
        `)
        .eq('id', serviceId)
        .single();

    if (svcError || !service) {
        throw new Error(`Failed to find service: ${svcError?.message || 'Not found'}`);
    }

    // 2. Auto-release active workers and mark service ended in DB via RPC
    const { data: rpcData, error: rpcError } = await supabase.rpc('end_service', {
        p_service_id: serviceId,
        p_end_date: effectiveEndDate,
    });
    if (rpcError) throw new Error(`Failed to end service: ${rpcError.message}`);

    // 3. Compute verified working days strictly from multi-worker attendance
    const workerIds = (service.service_worker_assignments || [])
        .map((a: any) => a.employee_id)
        .filter(Boolean);

    let verifiedDays = 0;
    if (workerIds.length > 0 && service.start_date) {
        const { data: attRecords } = await supabase
            .from('attendance')
            .select('worker_id, duty_date, status, is_half_day, is_absent')
            .in('worker_id', workerIds)
            .gte('duty_date', service.start_date.split('T')[0])
            .lte('duty_date', effectiveEndDate);

        if (attRecords && attRecords.length > 0) {
            verifiedDays = calculateClientServiceDaysFromAttendance(
                service.start_date.split('T')[0],
                effectiveEndDate,
                attRecords
            );
        }
    }

    // Fallback if no attendance records exist at all
    if (verifiedDays <= 0) {
        verifiedDays = rpcData?.total_lifetime_days || 1;
    }

    // 3. Compute calendar lifetime days as well as verified attendance days
    const startDateStr = service.start_date ? service.start_date.split('T')[0] : effectiveEndDate;
    const dStart = new Date(`${startDateStr}T00:00:00`);
    const dEnd = new Date(`${effectiveEndDate}T00:00:00`);
    const calendarDays = (!isNaN(dStart.getTime()) && !isNaN(dEnd.getTime()) && dEnd >= dStart)
        ? Math.round((dEnd.getTime() - dStart.getTime()) / (1000 * 60 * 60 * 24)) + 1
        : verifiedDays;

    // 30-Day Threshold Rule:
    // If service ended before completing 30 days (< 30 days), charge the incomplete month daily rate.
    // If service ran 30 or more days (>= 30 days), charge the standard complete month daily rate.
    const isIncompleteMonth = calendarDays < 30 || verifiedDays < 30;

    // Fetch lead and quotation rates as fallback to ensure incomplete rate is accurately captured
    const [leadRateRes, quoteRateRes] = await Promise.all([
        supabase.from('crm_leads')
            .select('complete_month_daily_rate, incomplete_month_daily_rate, estimated_value_monthly')
            .eq('id', service.client_id)
            .maybeSingle(),
        supabase.from('crm_quotations')
            .select('complete_month_rate, incomplete_month_rate')
            .eq('lead_id', service.client_id)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle()
    ]);

    const completeDailyRate = Number(service.complete_month_daily_rate)
        || Number(leadRateRes.data?.complete_month_daily_rate)
        || Number(quoteRateRes.data?.complete_month_rate)
        || (leadRateRes.data?.estimated_value_monthly ? Math.round(Number(leadRateRes.data.estimated_value_monthly) / 30) : 500);

    const incompleteDailyRate = Number(service.incomplete_month_daily_rate)
        || Number(leadRateRes.data?.incomplete_month_daily_rate)
        || Number(quoteRateRes.data?.incomplete_month_rate)
        || (completeDailyRate > 0 ? completeDailyRate * 2 : 1000)
        || (service.hours_per_day === 24 ? 2000 : 1000);

    const rateUsed = isIncompleteMonth ? incompleteDailyRate : completeDailyRate;
    const rateTier: 'incomplete_month' | 'complete_month' = isIncompleteMonth ? 'incomplete_month' : 'complete_month';
    const tierReason = isIncompleteMonth
        ? `Service ended before 30 days (< 30 days) — billed at incomplete month daily rate of ₹${rateUsed.toLocaleString('en-IN')}/day`
        : `Service completed 30+ days (≥ 30 days) — billed at complete month daily rate of ₹${rateUsed.toLocaleString('en-IN')}/day`;

    const trueCost = verifiedDays * rateUsed;

    // Previously billed (exclude any final settlement bills)
    const { data: prevBills } = await supabase
        .from('service_bills')
        .select('id, amount, type')
        .eq('service_id', serviceId)
        .neq('type', 'final');

    const previouslyBilled = (prevBills || []).reduce((sum, b) => sum + (Number(b.amount) || 0), 0);
    const depositAmount = Number(service.deposit_amount) || 0;
    const totalAlreadyPaid = previouslyBilled + depositAmount;
    const settlement = trueCost - totalAlreadyPaid;

    // 4. Generate the Final Settlement Tax Invoice
    let invoiceNumber = '';
    let invoicePdfUrl = '';

    try {
        const clientName = (service.clients as any)?.client_name || 'Client';
        const clientPhone = (service.clients as any)?.phone_number || '';
        
        let clientAddress = '';
        let serviceCategory = service.service_type || 'Old Age Care';
        try {
            const [leadRes, consentRes] = await Promise.all([
                supabase.from('crm_leads').select('notes, service_interest').eq('id', service.client_id).maybeSingle(),
                supabase.from('client_consents').select('address, service_category, offered_time').eq('lead_id', service.client_id).order('created_at', { ascending: false }).limit(1).maybeSingle(),
            ]);
            if (consentRes.data?.address) clientAddress = consentRes.data.address;
            else if (leadRes.data?.notes) {
                const locMatch = leadRes.data.notes.match(/^Location:\s*(.+)$/im);
                if (locMatch) clientAddress = locMatch[1].trim();
            }
            if (consentRes.data?.service_category) serviceCategory = consentRes.data.service_category;
            else if (leadRes.data?.service_interest) serviceCategory = leadRes.data.service_interest;
        } catch {}

        invoiceNumber = `INV-${Math.floor(100000 + Math.random() * 900000)}`;
        const formatDateStr = (dStr: string) => {
            if (!dStr) return '';
            const [y, m, d] = dStr.split('-');
            return `${d}/${m}/${y}`;
        };
        const sDate = service.start_date ? service.start_date.split('T')[0] : effectiveEndDate;
        const periodStr = `${formatDateStr(sDate)} To ${formatDateStr(effectiveEndDate)}`;

        try {
            invoicePdfUrl = await generateAndUploadInvoicePdf({
                clientId: service.client_id,
                clientName: clientName,
                clientPhone: clientPhone,
                clientAddress: clientAddress,
                invoiceNumber: invoiceNumber,
                invoiceDate: effectiveEndDate,
                dueDate: effectiveEndDate,
                serviceName: `${service.hours_per_day || 10}-HOUR SHIFT (${serviceCategory})`,
                servicePeriod: periodStr,
                days: verifiedDays,
                ratePerDay: rateUsed,
                grossAmount: trueCost,
                previouslyBilled: previouslyBilled,
                depositCollected: depositAmount,
                settlementAmount: settlement,
                isFinalSettlement: true
            });
        } catch (clientPdfErr) {
            console.warn('Client-side PDF generation failed, falling back to function:', clientPdfErr);
            const invResp = await supabase.functions.invoke('generate-invoice', {
                body: {
                    lead_id: service.client_id,
                    manual_invoice: true,
                    client_name: clientName,
                    client_phone: clientPhone,
                    client_address: clientAddress,
                    service_name: serviceCategory,
                    service_hours: service.hours_per_day ? String(service.hours_per_day) : '24',
                    start_date: service.start_date ? service.start_date.split('T')[0] : effectiveEndDate,
                    end_date: effectiveEndDate,
                    days: verifiedDays,
                    rate_per_day: rateUsed,
                    deposit_collected: depositAmount,
                    previously_billed: previouslyBilled,
                    invoice_date: effectiveEndDate,
                }
            });

            if (invResp.data?.success) {
                invoiceNumber = invResp.data.invoice_number || invoiceNumber;
                invoicePdfUrl = invResp.data.public_url || '';
            }
        }
    } catch (genErr) {
        console.error('Final invoice generation error:', genErr);
    }

    // 5. Update or insert the final bill record in service_bills with invoice info
    const netFinalBillAmount = Math.max(0, settlement);
    const finalBillNotes = JSON.stringify({
        invoice_number: invoiceNumber || `INV-F${Math.floor(1000 + Math.random() * 9000)}`,
        invoice_pdf_url: invoicePdfUrl,
        status: settlement <= 0 ? 'settled' : 'pending',
        type: 'final_settlement',
        gross_amount: trueCost,
        previously_billed: previouslyBilled,
        net_amount: netFinalBillAmount,
        deposit: depositAmount,
        settlement_amount: settlement,
        refund_amount: settlement < 0 ? Math.abs(settlement) : 0,
        verified_days: verifiedDays,
        calendar_days: calendarDays,
        rate_used: rateUsed,
        rate_tier: rateTier,
        tier_reason: tierReason,
        is_incomplete_month: isIncompleteMonth,
        generated_at: new Date().toISOString()
    });

    const { data: finalBill } = await supabase
        .from('service_bills')
        .select('id')
        .eq('service_id', serviceId)
        .eq('type', 'final')
        .maybeSingle();

    if (finalBill?.id) {
        await supabase
            .from('service_bills')
            .update({
                total_days: verifiedDays,
                daily_rate_used: rateUsed,
                amount: netFinalBillAmount,
                deposit_applied: depositAmount,
                deposit_settled: true,
                settlement_amount: settlement,
                notes: finalBillNotes,
                period_end: effectiveEndDate
            })
            .eq('id', finalBill.id);
    } else {
        await supabase
            .from('service_bills')
            .insert({
                service_id: serviceId,
                period_start: service.start_date ? service.start_date.split('T')[0] : effectiveEndDate,
                period_end: effectiveEndDate,
                total_days: verifiedDays,
                daily_rate_used: rateUsed,
                amount: netFinalBillAmount,
                type: 'final',
                deposit_applied: depositAmount,
                deposit_settled: true,
                settlement_amount: settlement,
                notes: finalBillNotes
            });
    }

    // 6. Synchronize workers' payroll records: cap period_end at effectiveEndDate and mark type 'final'
    try {
        const { data: servicePayrolls } = await supabase
            .from('payroll')
            .select('id, worker_id, period_start, period_end, days_worked, daily_rate')
            .eq('service_id', serviceId);

        if (servicePayrolls && servicePayrolls.length > 0) {
            for (const p of servicePayrolls) {
                if (p.period_end && p.period_end > effectiveEndDate) {
                    await supabase
                        .from('payroll')
                        .update({
                            period_end: effectiveEndDate,
                            type: 'final'
                        })
                        .eq('id', p.id);
                }
            }
        }
    } catch (paySyncErr) {
        console.error('Failed to sync worker payroll dates on service end:', paySyncErr);
    }

    // 7. Ensure legacy worker_assignments for this service's workers are marked completed
    try {
        if (workerIds.length > 0) {
            await supabase
                .from('worker_assignments')
                .update({
                    assignment_status: 'completed',
                    end_date: effectiveEndDate
                })
                .eq('client_id', service.client_id)
                .in('employee_id', workerIds)
                .eq('assignment_status', 'active');
        } else if ((service as any).legacy_assignment_id) {
            await supabase
                .from('worker_assignments')
                .update({
                    assignment_status: 'completed',
                    end_date: effectiveEndDate
                })
                .eq('id', (service as any).legacy_assignment_id);
        }
    } catch (legacyAsgnErr) {
        console.warn('Failed to complete legacy worker_assignments on service end:', legacyAsgnErr);
    }

    // 7b. Explicitly ensure services table marks status: 'ended' and deposit_status: 'settled'
    try {
        await supabase
            .from('services')
            .update({
                status: 'ended',
                end_date: effectiveEndDate,
                deposit_status: 'settled',
                updated_at: new Date().toISOString()
            })
            .eq('id', serviceId);
    } catch (svcUpdateErr) {
        console.warn('Failed to update service deposit_status to settled in endService:', svcUpdateErr);
    }

    // 8. Move client lead in crm_leads to 'Closed Won' ONLY IF no other active services remain
    try {
        const leadId = (service as any).lead_id || service.client_id;
        if (leadId) {
            // Check if client has any other active services
            const { data: remainingServices } = await supabase
                .from('services')
                .select('id')
                .eq('client_id', service.client_id)
                .eq('status', 'active')
                .neq('id', serviceId);

            const hasOtherActiveServices = (remainingServices && remainingServices.length > 0);

            if (hasOtherActiveServices) {
                // Client still has other active services ongoing — log activity but DO NOT close the client
                await supabase.from('crm_lead_activity').insert([{
                    lead_id: leadId,
                    event_type: 'service_ended',
                    description: `Service "${service.service_type || 'Care Service'}" ended & settled (${remainingServices.length} other service(s) still active)`,
                    metadata: {
                        service_id: serviceId,
                        settlement_amount: settlement,
                        deposit_settled: depositAmount,
                        total_days: verifiedDays
                    }
                }]);
            } else {
                // All services for this client are now ended — move to Closed Won
                await supabase
                    .from('crm_leads')
                    .update({
                        pipeline_stage: 'Closed Won',
                        updated_at: new Date().toISOString()
                    })
                    .eq('id', leadId);

                await supabase.from('crm_lead_activity').insert([{
                    lead_id: leadId,
                    event_type: 'stage_changed',
                    description: `All services ended & deposit settled — moved to "Closed Won"`,
                    metadata: {
                        to: 'Closed Won',
                        from: 'Active Client',
                        service_id: serviceId,
                        settlement_amount: settlement,
                        deposit_settled: depositAmount,
                        total_days: verifiedDays
                    }
                }]);
            }
        }
    } catch (crmSyncErr) {
        console.warn('Failed to update lead stage on service end:', crmSyncErr);
    }

    return {
        success: true,
        service_id: serviceId,
        client_id: service.client_id,
        client_name: (service.clients as any)?.client_name || 'Client',
        client_phone: (service.clients as any)?.phone_number || '',
        total_lifetime_days: verifiedDays,
        calendar_days: calendarDays,
        rate_used: rateUsed,
        rate_tier: rateTier,
        tier_reason: tierReason,
        is_incomplete_month: isIncompleteMonth,
        true_cost: trueCost,
        previously_billed: previouslyBilled,
        deposit: depositAmount,
        settlement: settlement,
        workers_released: rpcData?.workers_released || (service.service_worker_assignments?.length || 0),
        invoice_number: invoiceNumber,
        invoice_pdf_url: invoicePdfUrl
    };
}

// ── Generate Monthly Billing (calls RPC) ──────────────────

export interface MonthlyBillingResult {
    success: boolean;
    bills_created?: number;
    payslips_created?: number;
    period?: string;
    error?: string;
}

export async function generateMonthlyBilling(
    monthStart: string,
    monthEnd: string
): Promise<MonthlyBillingResult> {
    const { data, error } = await supabase.rpc('generate_monthly_billing', {
        p_month_start: monthStart,
        p_month_end: monthEnd,
    });

    if (error) throw new Error(`Failed to generate billing: ${error.message}`);

    // Automatic Deduplication Safeguard:
    // If a service already had an existing recorded/paid bill for this cycle, remove any unneeded duplicate rows
    try {
        const { data: bills } = await supabase
            .from('service_bills')
            .select('id, service_id, period_end, created_at, notes, type')
            .eq('period_end', monthEnd)
            .eq('type', 'recurring')
            .order('created_at', { ascending: false });

        if (bills && bills.length > 0) {
            // Sort so bills with paid status or PDF attachments are prioritized
            const sortedBills = [...bills].sort((a, b) => {
                let aPriority = 0;
                let bPriority = 0;
                try {
                    const pA = a.notes ? JSON.parse(a.notes) : {};
                    if (pA.status === 'paid') aPriority += 2;
                    if (pA.invoice_pdf_url) aPriority += 1;
                } catch {}
                try {
                    const pB = b.notes ? JSON.parse(b.notes) : {};
                    if (pB.status === 'paid') bPriority += 2;
                    if (pB.invoice_pdf_url) bPriority += 1;
                } catch {}
                return bPriority - aPriority;
            });

            const seenServiceIds = new Set<string>();
            const duplicatesToDelete: string[] = [];

            for (const b of sortedBills) {
                if (seenServiceIds.has(b.service_id)) {
                    duplicatesToDelete.push(b.id);
                } else {
                    seenServiceIds.add(b.service_id);
                }
            }

            if (duplicatesToDelete.length > 0) {
                await supabase.from('service_bills').delete().in('id', duplicatesToDelete);
            }
        }

        // Attendance Reconciliation:
        // Synchronize client billable days based on multi-worker attendance reality
        await syncClientBillsWithAttendance(monthStart, monthEnd);
    } catch (cleanErr) {
        console.warn('Billing deduplication/attendance safeguard note:', cleanErr);
    }

    return data as MonthlyBillingResult;
}

export async function syncClientBillsWithAttendance(monthStart: string, monthEnd: string) {
    try {
        const { data: services } = await supabase
            .from('services')
            .select(`
                id,
                start_date,
                end_date,
                complete_month_daily_rate,
                service_worker_assignments (
                    id,
                    employee_id,
                    start_date,
                    end_date
                )
            `)
            .eq('status', 'active');

        if (!services || services.length === 0) return;

        const now = new Date();
        const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

        for (const svc of services) {
            const workerIds = (svc.service_worker_assignments || [])
                .map((a: any) => a.employee_id)
                .filter(Boolean);

            const startStr = svc.start_date && svc.start_date > monthStart ? svc.start_date.split('T')[0] : monthStart;
            let effectiveEndStr = svc.end_date && svc.end_date < monthEnd ? svc.end_date.split('T')[0] : monthEnd;
            if (effectiveEndStr > todayStr) {
                effectiveEndStr = todayStr;
            }

            if (workerIds.length > 0 && effectiveEndStr >= startStr) {
                const { data: attRecords } = await supabase
                    .from('attendance')
                    .select('worker_id, duty_date, status, is_half_day, is_absent')
                    .in('worker_id', workerIds)
                    .gte('duty_date', startStr)
                    .lte('duty_date', effectiveEndStr);

                const actualDays = (attRecords && attRecords.length > 0)
                    ? calculateClientServiceDaysFromAttendance(startStr, effectiveEndStr, attRecords)
                    : 0;
                const rate = svc.complete_month_daily_rate || 500;
                const newAmount = actualDays * rate;

                const { data: currentBills } = await supabase
                    .from('service_bills')
                    .select('id, notes')
                    .eq('service_id', svc.id)
                    .eq('type', 'recurring')
                    .gte('period_end', monthStart);

                for (const b of currentBills || []) {
                    let isPaid = false;
                    try {
                        const n = b.notes ? JSON.parse(b.notes) : {};
                        if (n.status === 'paid') isPaid = true;
                    } catch {}

                    if (!isPaid) {
                        await supabase
                            .from('service_bills')
                            .update({
                                total_days: actualDays,
                                amount: newAmount,
                            })
                            .eq('id', b.id);
                    }
                }
            }
        }
    } catch (e) {
        console.warn('Sync client bills attendance error:', e);
    }
}

// ── Get Service Bills ─────────────────────────────────────

export async function getServiceBills(serviceId: string): Promise<ServiceBill[]> {
    const { data, error } = await supabase
        .from('service_bills')
        .select('*')
        .eq('service_id', serviceId)
        .order('created_at', { ascending: false });

    if (error) throw new Error(`Failed to fetch bills: ${error.message}`);
    return (data || []) as ServiceBill[];
}

// ── Record Service Invoice (Unified across CRM & Finance) ──

export interface RecordServiceInvoiceParams {
    serviceId: string;
    clientId?: string;
    periodStart: string;
    periodEnd: string;
    totalDays: number;
    dailyRateUsed: number;
    amount: number;
    invoiceNumber: string;
    invoicePdfUrl: string;
    type?: 'recurring' | 'final';
}

export async function recordServiceInvoice(params: RecordServiceInvoiceParams): Promise<ServiceBill | null> {
    try {
        let validServiceId = params.serviceId;

        // Verify validServiceId exists in public.services
        const { data: svcCheck } = await supabase
            .from('services')
            .select('id')
            .eq('id', validServiceId)
            .maybeSingle();

        if (!svcCheck && params.clientId) {
            // Find active service for client or find by legacy assignment
            const { data: fallbackSvc } = await supabase
                .from('services')
                .select('id')
                .or(`client_id.eq.${params.clientId},lead_id.eq.${params.clientId},legacy_assignment_id.eq.${params.serviceId}`)
                .eq('status', 'active')
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle();

            if (fallbackSvc?.id) {
                validServiceId = fallbackSvc.id;
            }
        }

        if (!validServiceId) {
            console.warn(`recordServiceInvoice: Cannot record invoice, no valid service found for ID ${params.serviceId}`);
            return null;
        }

        const metadata = {
            invoice_number: params.invoiceNumber,
            invoice_pdf_url: params.invoicePdfUrl,
            status: 'pending',
            generated_at: new Date().toISOString(),
        };

        // Check if an existing bill exists for this service and matching period
        const { data: existingBills } = await supabase
            .from('service_bills')
            .select('id, notes, amount, total_days')
            .eq('service_id', validServiceId)
            .eq('period_start', params.periodStart)
            .eq('period_end', params.periodEnd)
            .limit(1);

        let bill: any = null;

        if (existingBills && existingBills.length > 0) {
            const existing = existingBills[0];
            let existingNotes: any = {};
            try { existingNotes = existing.notes ? JSON.parse(existing.notes) : {}; } catch {}

            const mergedNotes = {
                ...existingNotes,
                invoice_number: params.invoiceNumber,
                invoice_pdf_url: params.invoicePdfUrl,
                generated_at: new Date().toISOString(),
                status: existingNotes.status === 'paid' ? 'paid' : 'pending',
            };

            const { data: updatedBill, error: updateError } = await supabase
                .from('service_bills')
                .update({
                    total_days: params.totalDays,
                    daily_rate_used: params.dailyRateUsed,
                    amount: params.amount,
                    notes: JSON.stringify(mergedNotes),
                })
                .eq('id', existing.id)
                .select()
                .single();

            if (updateError) console.error('Failed to update service_bill record:', updateError);
            bill = updatedBill;
        } else {
            const { data: insertedBill, error: billError } = await supabase
                .from('service_bills')
                .insert({
                    service_id: validServiceId,
                    period_start: params.periodStart,
                    period_end: params.periodEnd,
                    total_days: params.totalDays,
                    daily_rate_used: params.dailyRateUsed,
                    amount: params.amount,
                    type: params.type || 'recurring',
                    deposit_applied: 0,
                    deposit_settled: false,
                    notes: JSON.stringify(metadata),
                })
                .select()
                .single();

            if (billError) console.error('Failed to insert service_bill record:', billError);
            bill = insertedBill;
        }

        // Also update legacy worker_assignments / crm_leads if client_id is present
        if (params.clientId) {
            await supabase
                .from('worker_assignments')
                .update({
                    final_invoice_generated: true,
                    invoice_pdf_url: params.invoicePdfUrl,
                    final_invoice_number: params.invoiceNumber,
                    client_billing_rate: params.dailyRateUsed,
                })
                .eq('client_id', params.clientId);

            await supabase
                .from('crm_leads')
                .update({ pipeline_stage: 'Monthly Billing' })
                .eq('id', params.clientId);
        }

        return bill as ServiceBill;
    } catch (err) {
        console.error('Error recording service invoice:', err);
        return null;
    }
}

// ── Mark Service Bill Paid ────────────────────────────────

export interface MarkServiceBillPaidParams {
    billId?: string;
    serviceId?: string;
    clientName: string;
    amount: number;
    paymentMethod: string;
    transactionRef?: string;
    paymentDate?: string;
}

export async function markServiceBillPaid(params: MarkServiceBillPaidParams): Promise<boolean> {
    try {
        const txnRef = params.transactionRef?.trim() || `${params.paymentMethod.toUpperCase().replace(/\s+/g, '')}-${crypto.randomUUID().replace(/-/g, '').substring(0, 8).toUpperCase()}`;
        const payDate = params.paymentDate
            ? (params.paymentDate.includes('T') ? new Date(params.paymentDate).toISOString() : new Date(`${params.paymentDate}T12:00:00`).toISOString())
            : new Date().toISOString();

        // 1. Record in payments table
        await supabase.from('payments').insert({
            client_name: params.clientName,
            amount: params.amount,
            payment_type: 'service',
            transaction_ref: txnRef,
            payment_date: payDate,
            recorded_by: 'admin',
        });

        // 2. Update service_bills note metadata if billId is provided
        if (params.billId) {
            const { data: bill } = await supabase
                .from('service_bills')
                .select('notes')
                .eq('id', params.billId)
                .single();

            let notesObj: any = {};
            try {
                notesObj = bill?.notes ? JSON.parse(bill.notes) : {};
            } catch {
                notesObj = {};
            }

            notesObj.status = 'paid';
            notesObj.paid_at = payDate;
            notesObj.payment_method = params.paymentMethod;
            notesObj.transaction_ref = txnRef;

            await supabase
                .from('service_bills')
                .update({ notes: JSON.stringify(notesObj) })
                .eq('id', params.billId);
        }

        return true;
    } catch (err) {
        console.error('Error marking service bill paid:', err);
        return false;
    }
}
